import {
  BadgeDollarSign,
  Ban,
  Banknote,
  BriefcaseBusiness,
  ChartNoAxesColumnIncreasing,
  CircleDollarSign,
  CircleMinus,
  Clock3,
  CloudOff,
  Coins,
  House,
  Laptop,
  Lightbulb,
  LockKeyhole,
  MapPin,
  Monitor,
  PartyPopper,
  Rocket,
  Scissors,
  ShieldAlert,
  ShieldQuestion,
  Smartphone,
  Sparkles,
  Tablet,
  Target,
  ThumbsDown,
  ThumbsUp,
  TrendingUp,
  Trophy,
  WalletMinimal,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

import type {
  ViralizeQualificationField,
  ViralizeQualificationOptionForField,
} from '@/lib/viralize-qualification/questionnaire';

type QualificationOptionKey<Value extends string | boolean> = `${Value}`;

type QualificationOptionIconRegistry = {
  readonly [Field in ViralizeQualificationField]: Readonly<{
    readonly [Option in ViralizeQualificationOptionForField<Field> as QualificationOptionKey<Option>]: LucideIcon;
  }>;
};

export const QUALIFICATION_OPTION_ICONS = Object.freeze({
  motivation: Object.freeze({
    make_money: CircleDollarSign,
    recognition: Trophy,
    entertain_friends: PartyPopper,
    professional_growth: TrendingUp,
  } satisfies QualificationOptionIconRegistry['motivation']),
  starting_difficulty: Object.freeze({
    lack_of_time: Clock3,
    technical_difficulty: Wrench,
    creativity: Lightbulb,
    insecurity: ShieldQuestion,
  } satisfies QualificationOptionIconRegistry['starting_difficulty']),
  equipment: Object.freeze({
    phone_only: Smartphone,
    notebook: Laptop,
    tablet: Tablet,
    desktop: Monitor,
  } satisfies QualificationOptionIconRegistry['equipment']),
  monthly_income: Object.freeze({
    under_1000: WalletMinimal,
    up_to_2500: Coins,
    '2500_to_5000': Banknote,
    '5000_to_7000': ChartNoAxesColumnIncreasing,
    over_10000: BadgeDollarSign,
  } satisfies QualificationOptionIconRegistry['monthly_income']),
  clipper_experience: Object.freeze({
    not_clipper: Scissors,
    one_campaign: Target,
    some_campaigns: Trophy,
    lives_from_clips: BriefcaseBusiness,
  } satisfies QualificationOptionIconRegistry['clipper_experience']),
  could_earn_more: Object.freeze({
    false: CircleMinus,
    true: TrendingUp,
  } satisfies QualificationOptionIconRegistry['could_earn_more']),
  financially_stuck: Object.freeze({
    false: TrendingUp,
    true: LockKeyhole,
  } satisfies QualificationOptionIconRegistry['financially_stuck']),
  likes_current_job: Object.freeze({
    false: ThumbsDown,
    true: ThumbsUp,
  } satisfies QualificationOptionIconRegistry['likes_current_job']),
  wants_remote_mobile_work: Object.freeze({
    true: House,
    false: MapPin,
  } satisfies QualificationOptionIconRegistry['wants_remote_mobile_work']),
  self_employment_outlook: Object.freeze({
    dream: Sparkles,
    not_possible: CloudOff,
    already_self_employed_more_income: BadgeDollarSign,
  } satisfies QualificationOptionIconRegistry['self_employment_outlook']),
  first_30_day_revenue_outlook: Object.freeze({
    yes: Rocket,
    afraid: ShieldAlert,
    no: Ban,
  } satisfies QualificationOptionIconRegistry['first_30_day_revenue_outlook']),
} satisfies QualificationOptionIconRegistry);

export function qualificationOptionIcon<
  Field extends ViralizeQualificationField,
>(
  field: Field,
  option: ViralizeQualificationOptionForField<Field>,
): LucideIcon {
  const fieldRegistry = QUALIFICATION_OPTION_ICONS[field] as Readonly<
    Record<string, LucideIcon>
  >;
  return fieldRegistry[String(option)];
}
